import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#injectSecondaryMenu();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      this.#injectSecondaryMenu();
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }

  /**
   * Fallback: inject secondary header menu links into the drawer if not rendered server-side.
   */
  #injectSecondaryMenu() {
    const primaryMenu = this.querySelector('.menu-drawer__menu');
    if (!primaryMenu) return;

    const headerComponent = this.closest('header-component') ?? document.querySelector('header-component');
    if (!headerComponent) return;

    /** @type {HTMLAnchorElement[]} */
    let secondaryLinks = [];

    const rightColumn = headerComponent.querySelector('.header__column--right');
    if (rightColumn) {
      secondaryLinks = Array.from(rightColumn.querySelectorAll('nav[header-menu] a.menu-list__link'))
        .filter(link => link instanceof HTMLAnchorElement);
    }

    if (!secondaryLinks.length) {
      const allHeaderMenus = Array.from(headerComponent.querySelectorAll('header-menu'))
        .filter(menu => !menu.closest('.header__drawer') && !menu.closest('header-drawer'));

      if (allHeaderMenus.length >= 2) {
        const primaryMenuLinks = allHeaderMenus[0].querySelectorAll('a.menu-list__link');
        const primaryHrefs = Array.from(primaryMenuLinks).map(link => link.getAttribute('href'));

        allHeaderMenus.slice(1).forEach(menu => {
          const links = menu.querySelectorAll('a.menu-list__link');
          links.forEach(link => {
            const href = link.getAttribute('href');
            if (href && !primaryHrefs.includes(href) && link instanceof HTMLAnchorElement) {
              secondaryLinks.push(link);
            }
          });
        });
      }
    }

    if (!secondaryLinks.length) return;

    const existingSecondaryMenu = this.querySelector('.menu-drawer__menu--secondary');
    const secondaryMenu = existingSecondaryMenu ?? document.createElement('ul');
    if (!existingSecondaryMenu) {
      secondaryMenu.className = 'menu-drawer__menu menu-drawer__menu--secondary';
      secondaryMenu.setAttribute('role', 'list');
    }
    secondaryMenu.innerHTML = '';

    const offset = primaryMenu.children.length;

    secondaryLinks.forEach((link, index) => {
      if (!(link instanceof HTMLAnchorElement)) return;

      const href = link.getAttribute('href');
      if (!href) return;

      const li = document.createElement('li');
      li.className = 'menu-drawer__list-item menu-drawer__list-item--flat';
      li.style.setProperty('--menu-drawer-animation-index', String(offset + index + 1));

      const a = document.createElement('a');
      a.href = href;
      a.className = 'menu-drawer__menu-item menu-drawer__menu-item--mainlist menu-drawer__animated-element focus-inset';
      if (link.getAttribute('aria-current') === 'page') {
        a.setAttribute('aria-current', 'page');
      }

      const span = document.createElement('span');
      span.className = 'menu-drawer__menu-item-text wrap-text';
      span.textContent = link.textContent?.trim() ?? '';

      a.appendChild(span);
      li.appendChild(a);
      secondaryMenu.appendChild(li);
    });
    if (!secondaryMenu.children.length) return;

    if (!existingSecondaryMenu) {
      primaryMenu.insertAdjacentElement('afterend', secondaryMenu);
    }
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
